import api from 'api';
import { proxy, subscribe } from 'valtio';
import { derive, subscribeKey } from 'valtio/utils';
import dayjs from 'dayjs';
import storeRematch from '@/store';
import { periods } from '@/constants/reports';
import createCache from '@/restore/oil-production-loss/cache';

import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import * as versions from './versions';
import * as stratums from './stratums';

const ABORT_ERROR_CODE = 20;

const DATA = {
    [periods.ALL]: { label: 'За весь срок', format: 'MM.YYYY', picker: 'month' },
    [periods.MONTH]: { label: 'За месяц', format: 'MM.YYYY', picker: 'month' },
    [periods.YEAR]: { label: 'За год', format: 'YYYY', picker: 'year' },
    [periods.PERIOD]: { label: 'За период', format: 'MM.YYYY', picker: 'month' },
};

export const options = Object.values(periods).map(value => ({ value, label: DATA[value].label }));

const defaultState = {
    isLoading: false,
    interval: null,
    period: periods.ALL,
    date: null,
};

export const store = proxy({
    ...defaultState,
});

const createActions = () => {
    let kyController = new AbortController();
    const cache = createCache();

    return {
        getParams() {
            let date = store.date;

            if (store.period === periods.ALL) {
                date = store.interval;
            }

            return {
                period: store.period,
                date,
            };
        },
        resetDefaultState() {
            kyController.abort();
            kyController = new AbortController();

            Object.entries(defaultState).map(([key, value]) => {
                store[key] = value;
            });
        },
        needToDisableDate(currentDate) {
            if (isNil(store.interval)) return false;

            const { startDate, endDate } = store.interval;
            const start = dayjs(startDate);
            const end = dayjs(endDate);

            if (!start.isValid() || !end.isValid()) return false;

            return currentDate.isBefore(startDate, 'month') || currentDate.isAfter(endDate, 'month');
        },
        async fetchReportingInterval() {
            store.isLoading = true;

            kyController.abort();
            kyController = new AbortController();

            const { id: fieldId, parentVentureId: ventureId } = storeRematch.getState().tree.calculationObject ?? {};

            const { versionId } = versions.actions.getParams();
            const { stratumIds } = stratums.actions.getParams();

            if (isNil(versionId) || isEmpty(stratumIds)) {
                store.isLoading = false;
                return;
            }

            const payload = {
                body: { fieldId, ventureId, versionId, stratums: stratumIds },
                signal: kyController.signal,
            };

            try {
                const { interval } = await cache.get(payload, api.reports.getInterval);

                if (!interval) {
                    store.isLoading = false;
                    return;
                }

                store.interval = {
                    startDate: dayjs(interval.startDate, 'MM.YYYY').toDate(),
                    endDate: dayjs(interval.endDate, 'MM.YYYY').toDate(),
                };

                if (store.period === periods.ALL) {
                    store.date = [store.interval.startDate, store.interval.endDate];
                }

                store.isLoading = false;
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('::period report fetch error', error);
                if (error.code !== ABORT_ERROR_CODE) store.isLoading = false;
            }
        },
    };
};

export const actions = createActions();

export const handlers = {
    onSelectPeriodType(value) {
        store.period = value;

        if (value === periods.ALL || value === periods.PERIOD) {
            store.date = store.interval;
        } else {
            store.date = store.interval.endDate;
        }
    },
    onChangeDate(value) {
        if (!value) {
            store.date = dayjs();
            return;
        }
        store.date = value.toISOString();
    },

    onChangeRange(value) {
        if (!value || value.length !== 2 || !value[0] || !value[1]) {
            console.error('Invalid date provided in onChangeRange:', value);
            return;
        }
        const startDate = dayjs(value[0]);
        const endDate = dayjs(value[1]);
        if (startDate.isValid() && endDate.isValid()) {
            store.date = {
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
            };
        } else {
            console.error('Invalid range provided in onChangeRange:', value);
        }
    },
};

export const validate = {
    check() {
        const resultObject = {
            modelName: 'period',
            isValid: true,
        };

        const { period, date } = store;

        if (date || period === periods.ALL) {
            return resultObject;
        }

        resultObject.isValid = false;
        return resultObject;
    },
};

export const isPeriodLoading = proxy({
    isLoading: false,
});

subscribe(store, () => {
    isPeriodLoading.isLoading = store.isLoading || versions.store.isLoading;
});
subscribe(versions.store, () => {
    isPeriodLoading.isLoading = store.isLoading || versions.store.isLoading;
});
subscribe(stratums.store, () => {
    isPeriodLoading.isLoading = store.isLoading || versions.store.isLoading;
});

subscribeKey(stratums.store, 'stratums', () => {
    actions.fetchReportingInterval();
});

subscribeKey(store, 'interval', () => handlers.onChangeDate(null));
